import { useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Archive, ArchiveRestore, ArrowLeft, ArrowRightLeft, ChevronDown, CircleCheck, ImagePlus, MapPinOff, Pencil, Printer, Tag, TicketX } from 'lucide-react'
import { Alerta, Boton, Cargando, clasesBoton, Menu, OpcionMenu, Panel } from '../../components/kit'
import { BadgesBien } from '../../components/inventario/BadgesBien'
import { DialogoBaja } from '../../components/inventario/DialogoBaja'
import { DialogoBien } from '../../components/inventario/DialogoBien'
import { DialogoNoUbicado } from '../../components/inventario/DialogoNoUbicado'
import { DialogoSolicitarTraslado } from '../../components/inventario/DialogoSolicitarTraslado'
import { DialogoTraslado } from '../../components/inventario/DialogoTraslado'
import { DialogoEtiquetas } from '../../components/inventario/Etiquetas'
import { ControlGaleria, GaleriaFotos } from '../../components/inventario/GaleriaFotos'
import { Historial } from '../../components/inventario/Historial'
import { PanelTeletrabajo } from '../../components/inventario/Teletrabajo'
import { useAuth } from '../../contexts/AuthProvider'
import { useBien, useEditarBien, useHistorialBien, useMarcarUbicado } from '../../queries/useInventario'
import { mensajeDeError } from '../../utils/errores'
import { formatoFecha } from '../../utils/fecha'
import { ESTADO, ESTADO_FISICO, TIPO, ubicacion } from '../../utils/inventario'

/** La ficha de un bien: sus datos, lo que tiene pendiente y su historial. Quien no administra bienes la consulta y pide su traslado. */
export default function Bien() {
  const admin = useAuth().puede('administra_bienes')
  const id = Number(useParams().id)
  const bien = useBien(id)
  const historial = useHistorialBien(id)
  const editar = useEditarBien()
  const ubicar = useMarcarUbicado()
  const [dialogo, setDialogo] = useState<'editar' | 'trasladar' | 'solicitar' | 'no-ubicado' | 'baja' | 'revertir' | 'etiqueta' | null>(null)
  const galeria = useRef<ControlGaleria>(null)

  if (bien.isLoading) return <Cargando />
  if (bien.isError || !bien.data) return <Alerta tipo="error">{mensajeDeError(bien.error, 'No se encontró el bien.')}</Alerta>
  const b = bien.data
  const volver = b.recinto ? { a: `/app/oficinas/${b.recinto.codigo}`, texto: b.recinto.nombre } : { a: '/app/bienes', texto: 'Bienes' }
  // Lo que no se usa a cada rato va en «Más»; lo que saca o marca el bien, en rojo.
  const mas: OpcionMenu[] = [
    ...(b.etiqueta_pendiente ? [] : [{ clave: 'falta-etiqueta', texto: 'Falta etiqueta', Icono: TicketX, grupo: 0 }]),
    ...(b.estado === 'ACTIVO' && b.recinto ? [{ clave: 'no-ubicado', texto: 'No ubicado', Icono: MapPinOff, grupo: 1, peligro: true }] : []),
    { clave: 'baja', texto: 'Dar de baja', Icono: Archive, grupo: 1, peligro: true },
  ]
  const elegirMas = (clave: string) => {
    if (clave === 'falta-etiqueta') editar.mutate({ id: b.id, datos: { etiqueta_pendiente: true } })
    else setDialogo(clave as 'no-ubicado' | 'baja')
  }

  return (
    <div className="pila pila--amplia">
      <div>
        <Boton variante="text" tam="sm" icono={ArrowLeft} to={volver.a}>{volver.texto}</Boton>
      </div>
      <header className="pagina__cabecera pagina__cabecera--con-accion">
        <div>
          <h1 className="dcc-t-heading-m">{b.numero || 'Sin N°'} · {b.descripcion}</h1>
          <BadgesBien bien={b} />
        </div>
        {!admin && b.estado !== 'BAJA' && (
          <div className="acciones-pagina">
            <Boton icono={ArrowRightLeft} onClick={() => setDialogo('solicitar')}>Solicitar traslado</Boton>
          </div>
        )}
        {admin && (
        <div className="acciones-pagina">
          {/* Lo más común al revisar un bien: a la vista, aunque la galería esté cerrada (está al final). */}
          <Boton icono={ImagePlus} onClick={() => galeria.current?.subir()}>Subir fotos</Boton>
          <Boton icono={Pencil} variante="outlined" onClick={() => setDialogo('editar')}>Editar</Boton>
          {/* En baja no se traslada ni se marca: «Revertir baja» está en el aviso de abajo. */}
          {b.estado !== 'BAJA' && (
            <>
              <Boton icono={ArrowRightLeft} variante="outlined" onClick={() => setDialogo('trasladar')}>Trasladar</Boton>
              <Boton icono={Printer} variante="outlined" onClick={() => setDialogo('etiqueta')}>Imprimir etiqueta</Boton>
              <Menu
                etiqueta="Más acciones del bien"
                claseDisparador={clasesBoton('outlined', 'primary', 'md')}
                disparador={<>Más <ChevronDown className="dcc-i" size={18} aria-hidden /></>}
                alineado="fin"
                opciones={mas}
                onElegir={elegirMas}
              />
            </>
          )}
        </div>
        )}
      </header>

      {b.otros_con_mismo_numero.length > 0 && (
        <Alerta tipo="error" titulo="N° repetido">
          Otros bienes tienen el mismo N°:{' '}
          {b.otros_con_mismo_numero.map((o, i) => (
            <span key={o.id}>
              {i > 0 && ', '}
              <Link to={`/app/bienes/${o.id}`}>{o.descripcion} ({o.recinto?.nombre ?? 'sin ubicación'})</Link>
            </span>
          ))}
          . Corrige el N° del que esté mal.
        </Alerta>
      )}
      {b.estado === 'BAJA' && (
        <div className="aviso-accion">
          <Alerta tipo="info" titulo="Para dar de baja">
            Ya no aparece en la plancheta{b.recinto ? ` de ${b.recinto.nombre}` : ''}. Si se revierte, vuelve a{' '}
            {b.recinto ? 'esa oficina' : 'quedar sin oficina'}. El motivo está en el historial.
          </Alerta>
          {admin && <Boton icono={ArchiveRestore} variante="outlined" onClick={() => setDialogo('revertir')}>Revertir baja</Boton>}
        </div>
      )}
      {!b.recinto && b.estado !== 'BAJA' && (
        <Alerta tipo="error" titulo="Sin ubicación">La planilla no decía dónde está. Trasládalo a su oficina.</Alerta>
      )}
      {b.estado === 'NO_UBICADO' && (
        <div className="aviso-accion">
          <Alerta tipo="error" titulo="No ubicado">No estaba en {b.recinto?.nombre ?? 'su oficina'} cuando se revisó.</Alerta>
          {admin && b.recinto && (
            <Boton icono={CircleCheck} onClick={() => ubicar.mutate({ id: b.id, recinto: b.recinto!.codigo })} cargando={ubicar.isPending}>
              Apareció en {b.recinto.nombre}
            </Boton>
          )}
        </div>
      )}
      {b.etiqueta_pendiente && (
        <div className="aviso-accion">
          <Alerta tipo="info" titulo="Etiqueta pendiente">Falta pegarle la etiqueta con su N°.</Alerta>
          {admin && (
            <Boton icono={Tag} variante="outlined" onClick={() => editar.mutate({ id: b.id, datos: { etiqueta_pendiente: false } })} cargando={editar.isPending}>
              Etiqueta pegada
            </Boton>
          )}
        </div>
      )}
      {(editar.isError || ubicar.isError) && <Alerta tipo="error">{mensajeDeError(editar.error ?? ubicar.error)}</Alerta>}

      <div className="grilla">
        <Panel titulo="Datos">
          <dl className="datos">
            <dt>N° de bien</dt>
            <dd>{b.numero || '—'}</dd>
            <dt>Tipo</dt>
            <dd>{TIPO[b.tipo].texto}</dd>
            <dt>Descripción</dt>
            <dd>{b.descripcion}</dd>
            <dt>Marca</dt>
            <dd>{b.marca || '—'}</dd>
            <dt>Modelo</dt>
            <dd>{b.modelo || '—'}</dd>
            <dt>N° de serie</dt>
            <dd>{b.serie || '—'}</dd>
            <dt>Estado físico</dt>
            <dd>{ESTADO_FISICO[b.estado_fisico].texto}</dd>
            <dt>Estado</dt>
            <dd>{ESTADO[b.estado].texto}</dd>
            <dt>Oficina</dt>
            <dd>
              {b.recinto ? (
                <>
                  <Link to={`/app/oficinas/${b.recinto.codigo}`}>{b.recinto.nombre}</Link>
                  <span className="tabla__sub">{b.recinto.codigo} · {ubicacion(b.recinto)}</span>
                </>
              ) : (
                'Sin ubicación'
              )}
            </dd>
            <dt>Observación</dt>
            <dd className="texto-preformateado">{b.observacion || '—'}</dd>
            <dt>Último cambio</dt>
            <dd>{formatoFecha(b.fecha_modificacion)}</dd>
          </dl>
        </Panel>
        <Panel titulo="Historial">
          {historial.isLoading && <Cargando />}
          {historial.isError && <Alerta tipo="error">{mensajeDeError(historial.error)}</Alerta>}
          {historial.data && <Historial movimientos={historial.data} enRecinto={false} />}
        </Panel>
      </div>

      {admin && <PanelTeletrabajo bien={b} />}
      {!admin && b.teletrabajo && <Alerta tipo="info" titulo="Teletrabajo">Alguien se lo lleva a la casa algunos días de la semana.</Alerta>}

      <GaleriaFotos bien={b} ref={galeria} soloLectura={!admin} />

      {dialogo === 'editar' && <DialogoBien bien={b} onCerrar={() => setDialogo(null)} />}
      {dialogo === 'trasladar' && <DialogoTraslado bienes={[b]} onCerrar={() => setDialogo(null)} />}
      {dialogo === 'solicitar' && <DialogoSolicitarTraslado bienes={[b]} onCerrar={() => setDialogo(null)} />}
      {dialogo === 'no-ubicado' && <DialogoNoUbicado bien={b} onCerrar={() => setDialogo(null)} />}
      {dialogo === 'etiqueta' && <DialogoEtiquetas bienes={[b]} onCerrar={() => setDialogo(null)} />}
      {(dialogo === 'baja' || dialogo === 'revertir') && <DialogoBaja bienes={[b]} revertir={dialogo === 'revertir'} onCerrar={() => setDialogo(null)} />}
    </div>
  )
}
